import React from "react";
import { Link } from "react-router-dom";
import { Handshake, ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import Reveal from "../components/Reveal";
import { Button } from "../components/Button";
import { CLIENTS, IMAGES } from "../data";

export default function Clients() {
  return (
    <div data-testid="clients-page">
      <PageHero
        eyebrow="Our Valued Clients"
        title="Trusted by industry leaders."
        subtitle="We are privileged to work alongside India's most respected renewable energy, power and heavy industry organisations — delivering critical logistics on projects of national importance."
        image={IMAGES.serviceHeavy}
      />

      <section className="py-24 md:py-32 bg-white" data-testid="clients-grid">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 mb-14">
            <div className="lg:col-span-6">
              <Reveal><p className="section-tag">Client Portfolio</p></Reveal>
              <Reveal delay={100}>
                <h2 className="font-heading text-4xl md:text-6xl leading-[0.95] tracking-tightest mt-6 text-brand-ink">
                  Partners powering<br />India's clean energy transition.
                </h2>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:pt-8">
              <Reveal delay={200}>
                <p className="font-body text-lg text-zinc-700 leading-relaxed">
                  Our clients span the entire value chain of India's wind energy sector — from OEMs and IPPs to
                  utilities and infrastructure majors. Each engagement is a long-term relationship built on
                  performance and trust.
                </p>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-0 border-t border-l border-zinc-200">
            {CLIENTS.map((c, i) => (
              <Reveal key={c} delay={i * 60}>
                <div
                  className="group border-r border-b border-zinc-200 p-8 min-h-[180px] flex flex-col justify-between hover:bg-brand-green hover:text-white transition-colors"
                  data-testid={`client-card-${i}`}
                >
                  <p className="font-mono text-[10px] uppercase tracking-widest text-brand-orange">
                    Client {String(i + 1).padStart(2, "0")}
                  </p>
                  <p className="font-heading text-2xl md:text-3xl leading-tight tracking-tight mt-6">
                    {c}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIAL-STYLE STRIP */}
      <section className="py-24 md:py-32 bg-brand-smoke" data-testid="clients-values">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              { k: "10+", v: "Blue-chip Clients", body: "Actively serving India's leading wind & power companies." },
              { k: "10,000+", v: "Successful Movements", body: "ODC and inter-carting operations delivered on schedule." },
              { k: "Pan-India", v: "Project Reach", body: "Executing across every major wind-energy corridor in India." },
            ].map((s, i) => (
              <Reveal key={s.v} delay={i * 120}>
                <div className="bg-white border-t-4 border-brand-green p-10" data-testid={`clients-stat-${i}`}>
                  <p className="font-heading text-6xl text-brand-green leading-none">{s.k}</p>
                  <p className="font-heading text-xl mt-4 text-brand-ink">{s.v}</p>
                  <p className="font-body text-sm text-zinc-600 mt-3 leading-relaxed">{s.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-brand-ink text-white" data-testid="clients-cta">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-3">
              <Handshake className="w-6 h-6 text-brand-orange" />
              <p className="font-mono text-xs uppercase tracking-widest text-brand-orange">Partner With Us</p>
            </div>
            <h2 className="font-heading text-3xl md:text-5xl leading-[0.95] tracking-tightest mt-4">
              Become the next name on this list.
            </h2>
            <p className="font-body text-lg mt-4 text-zinc-300 max-w-2xl">
              Reliability, safety and execution excellence — that's what our clients value. Talk to us about your next project.
            </p>
          </div>
          <div className="lg:col-span-4">
            <Link to="/contact" data-testid="clients-cta-contact">
              <Button variant="secondary" size="lg" className="w-full">Start a Conversation <ArrowRight className="w-4 h-4" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
